// @ts-check
// Painel lateral do editor de widgets (doc 16, seção 9): primeiro o tipo (Número, Barras, Linhas, Pizza, Tabela, Texto,
// Página incorporada, Indicador educacional e relatórios) e depois a configuração na ordem do contrato (objeto, filtro,
// operação e campo, agrupamento e granularidade, séries, ordenação, aparência, período do painel e filtros próprios do
// widget). Cada mudança vai direto ao rascunho e a pré-visualização (aqui e na grade) pede os dados de novo.
import { html } from 'htm/preact';
import { GEO_MAP_METRICS } from '../../../../shared/geo/geo-metrics.js';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton } from '../../ui/button.js';
import { Dropdown, MenuItem } from '../../ui/dropdown.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { objectsByNameSingular, userObjectsState } from '../../data/metadata-store.js';
import {
  FIELD_TYPE,
  getFieldIcon,
  getLabelIdentifierField,
  isFieldDisplayable,
  isFieldFilterable,
  isManyToOneRelation,
} from '../../lib/field-metadata.js';
import { buildFilterTree, getDefaultOperand, getFilterDisplayValue, operandNeedsValue } from '../../lib/view-filters.js';
import { generateId } from '../../lib/utils.js';
import { FieldSelectMenu, FilterEditor } from '../views/filter-editor.js';
import { closeSidePanel, sidePanelState } from '../side-panel/side-panel-state.js';
import { RichTextEditor } from '../record-show/rich-text-editor.js';
import { WidgetFiltersSection } from './widget-filters-editor.js';
import { checkIframeUrlForThisApp } from './iframe-check.js';
import { WIDGET_TYPES_WITHOUT_FILTERS } from '../../../../shared/dashboards/widget-filters.js';
import {
  addDraftWidget,
  changeDraftWidgetType,
  draftWidgetsState,
  duplicateDraftWidget,
  moveDraftWidgetToTab,
  removeDraftWidget,
  tabsState,
  requestFiltersState,
  setDraftWidgetRectangle,
  updateDraftConfiguration,
  updateDraftWidget,
} from './dashboard-state.js';
import { EditorField, EditorSection, SegmentedControl, SelectControl, ToggleRow } from './editor-controls.js';
import { WidgetCard } from './widget-card.js';
import { KPI_METRICS, getKpiMetric } from './widget-format.js';
import { DEPRECATED_REPORTS, LEAD_QUALITY_REPORTS, LIMITED_REPORTS, REPORTS_BY_WIDGET_TYPE, isReportWidgetType } from './report-widgets.js';
import { LeadQualityMeasuresEditor } from './lead-quality-measures-editor.js';
import { LEAD_QUALITY_FIELD_ENTITIES } from '../../../../shared/dashboards/lead-quality-measures.js';
import { MAIN_COLOR_NAMES } from '../../lib/theme-colors.js';
import { DEFAULT_WIDGET_SIZES } from '../../lib/dashboard-grid.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/dashboards.js').WidgetType} WidgetType
 * @typedef {import('./dashboard-state.js').DraftWidget} DraftWidget
 * @typedef {{ id: string, fieldName: string, operand: string, value: string, subFieldName?: string | null, displayValue?: string }} WidgetFilterItem
 */

/** Ordem do seletor de tipo (contrato, seção 6). */
export const WIDGET_TYPE_CHOICES = /** @type {const} */ ([
  { type: 'AGGREGATE', icon: 'IconSum' },
  { type: 'BAR', icon: 'IconChartBar' },
  { type: 'LINE', icon: 'IconChartLine' },
  { type: 'PIE', icon: 'IconChartDonut' },
  { type: 'TABLE', icon: 'IconTable' },
  { type: 'TEXT', icon: 'IconTypography' },
  { type: 'IFRAME', icon: 'IconFrame' },
  { type: 'KPI', icon: 'IconSchool' },
  { type: 'STAT_GROUP', icon: 'IconNumbers' },
  { type: 'BAR_LIST', icon: 'IconListDetails' },
  { type: 'DISTRIBUTION', icon: 'IconHourglass' },
  { type: 'FUNNEL_FLOW', icon: 'IconFilter' },
  { type: 'STAGE_FLOW', icon: 'IconLayoutKanban' },
  { type: 'UPCOMING_TABLE', icon: 'IconCalendarTime' },
  { type: 'GEO_MAP', icon: 'IconMapPin' },
]);

const GROUPABLE_FIELD_TYPES = new Set(['SELECT', 'MULTI_SELECT', 'BOOLEAN', 'DATE', 'DATE_TIME', 'RATING', 'TEXT', 'NUMBER']);
const NUMERIC_FIELD_TYPES = new Set(['NUMBER', 'NUMERIC', 'CURRENCY']);
const DATE_FIELD_TYPES = new Set(['DATE', 'DATE_TIME']);
const TABLE_FIELD_TYPES = new Set(['TEXT', 'FULL_NAME', 'EMAILS', 'PHONES', 'NUMBER', 'NUMERIC', 'CURRENCY', 'DATE', 'DATE_TIME', 'SELECT',
  'MULTI_SELECT', 'BOOLEAN', 'RATING', 'LINKS', 'RELATION', 'ADDRESS']);
// as mesmas operações do Twenty (AggregateOperations): contagens por campo e percentuais além de soma e média
const OPERATIONS = ['COUNT', 'COUNT_UNIQUE_VALUES', 'COUNT_EMPTY', 'COUNT_NOT_EMPTY', 'PERCENTAGE_EMPTY', 'PERCENTAGE_NOT_EMPTY', 'SUM', 'AVG', 'MIN', 'MAX',
  'COUNT_TRUE', 'COUNT_FALSE'];
const FIELD_COUNT_OPERATIONS = new Set(['COUNT_UNIQUE_VALUES', 'COUNT_EMPTY', 'COUNT_NOT_EMPTY', 'PERCENTAGE_EMPTY', 'PERCENTAGE_NOT_EMPTY']);
const DATE_GRANULARITIES = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'];
const ORDER_OPTIONS = ['FIELD_POSITION_ASC', 'FIELD_ASC', 'FIELD_DESC', 'VALUE_DESC', 'VALUE_ASC'];
const PREFERRED_OBJECTS = ['opportunityStudent', 'enrollmentOpportunity', 'person'];
const SYSTEM_GROUPABLE_FIELD_NAMES = new Set(['createdAt', 'updatedAt']);

/**
 * @param {FieldMetadata} field
 */
function isGroupableField(field) {
  if (!isFieldDisplayable(field)) return false;
  if (field.isSystem && !SYSTEM_GROUPABLE_FIELD_NAMES.has(field.name)) return false;
  return GROUPABLE_FIELD_TYPES.has(field.type) || isManyToOneRelation(field);
}

/**
 * Campos aceitos por operação: soma e média só em número e moeda; mínimo e máximo também em datas; "verdadeiros" em booleanos.
 * @param {FieldMetadata} field
 * @param {string} operation
 */
function isFieldValidForOperation(field, operation) {
  if (!isFieldDisplayable(field) || (field.isSystem && !SYSTEM_GROUPABLE_FIELD_NAMES.has(field.name))) return false;
  if (operation === 'SUM' || operation === 'AVG') return NUMERIC_FIELD_TYPES.has(field.type);
  if (operation === 'MIN' || operation === 'MAX') return NUMERIC_FIELD_TYPES.has(field.type) || DATE_FIELD_TYPES.has(field.type);
  if (operation === 'COUNT_TRUE' || operation === 'COUNT_FALSE') return field.type === FIELD_TYPE.BOOLEAN;
  if (FIELD_COUNT_OPERATIONS.has(operation)) return field.type !== FIELD_TYPE.RELATION || isManyToOneRelation(field);
  return false;
}

/**
 * @param {FieldMetadata} field
 */
function toFieldOption(field) {
  return { value: field.name, label: translateMetadataLabel(field.label), icon: getFieldIcon(field) };
}

function getDefaultObject() {
  const objects = userObjectsState.value;
  return PREFERRED_OBJECTS.map((name) => objects.find((objectMetadata) => objectMetadata.nameSingular === name)).find(Boolean) ?? objects[0];
}

/**
 * Configuração inicial de cada tipo, já com dados para a pré-visualização aparecer.
 * @param {WidgetType} type
 * @param {Record<string, any>} [previous] configuração anterior, para manter a fonte ao trocar o tipo
 */
export function buildDefaultConfiguration(type, previous = {}) {
  // filtros próprios acompanham a troca de tipo (menos para texto e página incorporada, que não têm dados)
  const widgetFilters = WIDGET_TYPES_WITHOUT_FILTERS.includes(type) ? undefined : previous.widgetFilters;
  if (isReportWidgetType(type)) return { report: REPORTS_BY_WIDGET_TYPE[type][0], widgetFilters };
  const objectMetadata = (previous.objectName && objectsByNameSingular.value.get(previous.objectName)) || getDefaultObject();
  const objectName = objectMetadata?.nameSingular;
  const fields = objectMetadata?.fields ?? [];
  // etapa e origem primeiro: são os agrupamentos que a escola mais usa
  const firstSelect = ['effectiveStage', 'stage', 'leadSource'].map((name) => fields.find((field) => field.name === name && isGroupableField(field))).find(Boolean)
    ?? fields.find((field) => field.type === FIELD_TYPE.SELECT && isGroupableField(field));
  const shared = { objectName, filter: previous.filter, filterItems: previous.filterItems, widgetFilters };
  switch (type) {
    case 'AGGREGATE':
      return { ...shared, operation: 'COUNT', format: 'NUMBER' };
    case 'BAR':
      return { ...shared, operation: 'COUNT', groupByFieldName: previous.groupByFieldName ?? firstSelect?.name, layout: 'VERTICAL', orderBy: 'FIELD_POSITION_ASC', omitNullValues: true };
    case 'LINE':
      return { ...shared, operation: 'COUNT', groupByFieldName: 'createdAt', dateGranularity: 'WEEK', dateFieldName: 'createdAt' };
    case 'PIE':
      return { ...shared, operation: 'COUNT', groupByFieldName: previous.groupByFieldName ?? firstSelect?.name, showCenterMetric: true, orderBy: 'VALUE_DESC' };
    case 'TABLE': {
      const labelField = objectMetadata ? getLabelIdentifierField(objectMetadata) : undefined;
      const tableFields = [labelField, ...fields.filter((field) => field !== labelField && TABLE_FIELD_TYPES.has(field.type) && !field.isSystem && isFieldDisplayable(field)
        && (field.type !== FIELD_TYPE.RELATION || isManyToOneRelation(field)))].filter(Boolean).slice(0, 4);
      return { ...shared, fields: tableFields.map((field) => /** @type {FieldMetadata} */ (field).name), limit: 10 };
    }
    case 'TEXT':
      return { markdown: '' };
    case 'IFRAME':
      return { url: '' };
    case 'KPI':
    default:
      return { metric: 'NEW_LEADS', widgetFilters };
  }
}

/**
 * @param {WidgetFilterItem[]} items
 * @param {ObjectMetadata} objectMetadata
 */
function buildWidgetFilter(items, objectMetadata) {
  const fieldsByName = new Map(objectMetadata.fields.map((field) => [field.name, field]));
  const viewFilters = items.map((item) => ({ ...item, fieldMetadataId: fieldsByName.get(item.fieldName)?.id ?? '' }));
  const fieldsById = new Map(objectMetadata.fields.map((field) => [field.id, field]));
  return buildFilterTree(viewFilters, (id) => fieldsById.get(id));
}

/**
 * Filtros do widget como chips, com o mesmo editor da barra de view.
 * @param {{ widget: DraftWidget, objectMetadata: ObjectMetadata }} props
 */
function WidgetFilterEditor({ widget, objectMetadata }) {
  const addAnchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const chipAnchors = useRef(/** @type {Record<string, HTMLElement | null>} */ ({}));
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [openId, setOpenId] = useState(/** @type {string | null} */ (null));
  const configuration = widget.configuration;
  /** @type {WidgetFilterItem[]} */
  const items = Array.isArray(configuration.filterItems) ? configuration.filterItems : [];
  const hasExternalFilter = Boolean(configuration.filter) && items.length === 0;
  const fieldsByName = new Map(objectMetadata.fields.map((field) => [field.name, field]));
  const commit = (/** @type {WidgetFilterItem[]} */ nextItems) => {
    updateDraftConfiguration(widget.key, { filterItems: nextItems, filter: buildWidgetFilter(nextItems, objectMetadata) ?? undefined });
  };
  const closeChip = (/** @type {WidgetFilterItem} */ item) => {
    setOpenId(null);
    const field = fieldsByName.get(item.fieldName);
    const current = items.find((candidate) => candidate.id === item.id);
    if (current && field && operandNeedsValue(current.operand) && !current.value && field.type !== FIELD_TYPE.BOOLEAN) commit(items.filter((candidate) => candidate.id !== item.id));
  };

  return html`<div class="widget-filter-editor">
    ${hasExternalFilter
      ? html`<div class="widget-editor-note">
          <${Icon} name="IconFilter" size=${14} />
          <span>${t('dashboards.editor.presetFilter')}</span>
          <button type="button" class="widget-message-link" onClick=${() => updateDraftConfiguration(widget.key, { filter: undefined, filterItems: [] })}>${t('dashboards.editor.clearFilter')}</button>
        </div>`
      : null}
    <div class="widget-filter-chips">
      ${items.map((item) => {
        const field = fieldsByName.get(item.fieldName);
        if (!field) return null;
        const viewFilter = { ...item, fieldMetadataId: field.id };
        return html`<div class="sort-or-filter-chip" key=${item.id} ref=${(element) => (chipAnchors.current[item.id] = element)}>
          <button type="button" class="sort-or-filter-chip-main" onClick=${() => setOpenId(openId === item.id ? null : item.id)}>
            <${Icon} name=${getFieldIcon(field)} size=${14} />
            <span class="sort-or-filter-chip-label">${translateMetadataLabel(field.label)}</span>
            <span class="sort-or-filter-chip-value">${t(`filters.operandsShort.${item.operand}`)}${getFilterDisplayValue(viewFilter, field)}</span>
          </button>
          <button type="button" class="sort-or-filter-chip-remove" aria-label=${t('viewBar.removeFilter')} onClick=${() => commit(items.filter((candidate) => candidate.id !== item.id))}>
            <${Icon} name="IconX" size=${14} />
          </button>
          <${Dropdown} anchorRef=${{ current: chipAnchors.current[item.id] ?? null }} isOpen=${openId === item.id} onClose=${() => closeChip(item)} width=${field.type === 'RELATION' ? 280 : 240}>
            <${FilterEditor}
              filter=${viewFilter}
              field=${field}
              onChange=${(changes) => commit(items.map((candidate) => (candidate.id === item.id ? { ...candidate, ...changes } : candidate)))}
              onClose=${() => closeChip(item)}
            />
          <//>
        </div>`;
      })}
      <button ref=${addAnchorRef} type="button" class="view-bar-add-filter" onClick=${() => setIsAddOpen(!isAddOpen)}>
        <${Icon} name="IconPlus" size=${14} />
        <span>${t('viewBar.addFilter')}</span>
      </button>
      <${Dropdown} anchorRef=${addAnchorRef} isOpen=${isAddOpen} onClose=${() => setIsAddOpen(false)} width=${300}>
        <${FieldSelectMenu}
          title=${t('viewBar.filter')}
          fields=${objectMetadata.fields.filter((field) => isFieldFilterable(field))}
          visibleFieldIds=${new Set()}
          onClose=${() => setIsAddOpen(false)}
          onSelect=${(field) => {
            setIsAddOpen(false);
            const item = { id: generateId(), fieldName: field.name, operand: getDefaultOperand(field), value: '' };
            commit([...items, item]);
            setOpenId(item.id);
          }}
        />
      <//>
    </div>
  </div>`;
}

/**
 * Lista de colunas da tabela: marcar para incluir; a ordem é a da marcação.
 * @param {{ widget: DraftWidget, objectMetadata: ObjectMetadata }} props
 */
function TableFieldsEditor({ widget, objectMetadata }) {
  /** @type {string[]} */
  const selected = Array.isArray(widget.configuration.fields) ? widget.configuration.fields : [];
  const candidates = objectMetadata.fields.filter((field) => TABLE_FIELD_TYPES.has(field.type) && isFieldDisplayable(field)
    && (!field.isSystem || SYSTEM_GROUPABLE_FIELD_NAMES.has(field.name)) && (field.type !== FIELD_TYPE.RELATION || isManyToOneRelation(field)));
  const ordered = [...selected.map((name) => candidates.find((field) => field.name === name)).filter(Boolean), ...candidates.filter((field) => !selected.includes(field.name))];
  return html`<div class="widget-editor-checklist">
    ${ordered.map((field) => {
      const fieldMetadata = /** @type {FieldMetadata} */ (field);
      const isSelected = selected.includes(fieldMetadata.name);
      return html`<${MenuItem}
        key=${fieldMetadata.name}
        multiSelect=${true}
        selected=${isSelected}
        icon=${getFieldIcon(fieldMetadata)}
        text=${translateMetadataLabel(fieldMetadata.label)}
        onClick=${() => updateDraftConfiguration(widget.key, {
          fields: isSelected ? selected.filter((name) => name !== fieldMetadata.name) : [...selected, fieldMetadata.name],
        })}
      />`;
    })}
  </div>`;
}

/**
 * @param {{ widget: DraftWidget }} props
 */
function WidgetConfigurationForm({ widget }) {
  const configuration = widget.configuration ?? {};
  const type = widget.type;
  const objectMetadata = configuration.objectName ? objectsByNameSingular.value.get(configuration.objectName) : undefined;
  const fields = objectMetadata?.fields ?? [];
  const fieldByName = (/** @type {string | undefined} */ name) => fields.find((field) => field.name === name);
  const set = (/** @type {Record<string, any>} */ changes) => updateDraftConfiguration(widget.key, changes);
  const usesObject = type === 'AGGREGATE' || type === 'BAR' || type === 'LINE' || type === 'PIE' || type === 'TABLE';
  const isChart = type === 'BAR' || type === 'LINE' || type === 'PIE';
  const operation = configuration.operation ?? 'COUNT';
  const groupByField = fieldByName(configuration.groupByFieldName);
  const isDateGroup = groupByField ? DATE_FIELD_TYPES.has(groupByField.type) : false;
  const dateFields = fields.filter((field) => DATE_FIELD_TYPES.has(field.type) && isFieldDisplayable(field) && (!field.isSystem || SYSTEM_GROUPABLE_FIELD_NAMES.has(field.name)));
  const objectOptions = userObjectsState.value.map((candidate) => ({ value: candidate.nameSingular, label: translateMetadataLabel(candidate.labelPlural), icon: candidate.icon ?? 'IconCube' }));
  const colorOptions = MAIN_COLOR_NAMES.filter((name) => name !== 'gray').map((name) => ({ value: name, label: t(`dashboards.colors.${name}`), icon: 'IconCircle' }));

  /**
   * @param {string} nextOperation
   */
  const changeOperation = (nextOperation) => {
    const currentField = fieldByName(configuration.fieldName);
    const keepField = currentField && nextOperation !== 'COUNT' && isFieldValidForOperation(currentField, nextOperation);
    const nextField = keepField ? currentField : nextOperation === 'COUNT' ? undefined : fields.find((field) => isFieldValidForOperation(field, nextOperation));
    set({
      operation: nextOperation,
      fieldName: nextField?.name,
      subFieldName: nextField?.type === FIELD_TYPE.CURRENCY ? 'amountMicros' : undefined,
      ...(type === 'AGGREGATE' ? { format: nextOperation.startsWith('PERCENTAGE') ? 'PERCENT' : nextField?.type === FIELD_TYPE.CURRENCY && !FIELD_COUNT_OPERATIONS.has(nextOperation) ? 'CURRENCY'
        : configuration.format === 'CURRENCY' || configuration.format === 'PERCENT' ? 'NUMBER' : configuration.format ?? 'NUMBER' } : {}),
    });
  };

  return html`
    ${type === 'KPI'
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.indicator')}>
          <${EditorField} label=${t('dashboards.editor.metric')} icon="IconSchool">
            <${SelectControl}
              value=${configuration.metric}
              searchable=${true}
              options=${KPI_METRICS.map((metric) => ({ value: metric.metric, label: t(`dashboards.metrics.${metric.metric}.label`), icon: metric.icon,
                group: t(`dashboards.editor.metricKinds.${metric.kind}`) }))}
              onChange=${(metric) => {
                if (!metric) return;
                const definition = getKpiMetric(metric);
                const previousDefinition = getKpiMetric(configuration.metric);
                const shouldRename = !widget.title || widget.title === t(`dashboards.metrics.${configuration.metric}.label`) || widget.title === t('dashboards.widgetTypes.KPI');
                updateDraftWidget(widget.key, {
                  configuration: { metric, ...(configuration.widgetFilters ? { widgetFilters: configuration.widgetFilters } : {}) },
                  ...(shouldRename ? { title: t(`dashboards.metrics.${metric}.label`) } : {}),
                });
                if (definition && previousDefinition && definition.kind !== previousDefinition.kind) {
                  const size = definition.kind === 'NUMBER' ? DEFAULT_WIDGET_SIZES.KPI : definition.kind === 'TABLE' ? DEFAULT_WIDGET_SIZES.TABLE : DEFAULT_WIDGET_SIZES.BAR;
                  setDraftWidgetRectangle(widget.key, { gridRow: widget.gridRow, gridColumn: Math.min(widget.gridColumn, 12 - size.columnSpan), ...size });
                }
              }}
            />
          <//>
          <p class="widget-editor-hint">${t(`dashboards.metrics.${configuration.metric}.description`)}</p>
        <//>`
      : null}

    ${isReportWidgetType(type)
      ? html`<${EditorSection} title=${t('reports.editor.section')}>
          <${EditorField} label=${t('reports.editor.report')} icon="IconReportAnalytics">
            <${SelectControl}
              value=${configuration.report}
              options=${(REPORTS_BY_WIDGET_TYPE[type] ?? [])
                // relatórios antigos (qualidade dos dados de antes) só aparecem para o cartão que já os usa
                .filter((report) => !DEPRECATED_REPORTS.has(report) || report === configuration.report)
                .map((report) => ({ value: report, label: t(`reports.names.${report}`) }))}
              onChange=${(report) => {
                if (!report || report === configuration.report) return;
                const shouldRename = !widget.title || widget.title === t(`reports.names.${configuration.report}`) || widget.title === t(`dashboards.widgetTypes.${type}`);
                updateDraftWidget(widget.key, {
                  configuration: {
                    report,
                    ...(report === 'LEAD_QUALITY_FIELDS' ? { entity: 'ALL' } : {}),
                    // a escolha de campos acompanha a troca entre cartões de qualidade
                    ...(LEAD_QUALITY_REPORTS.has(report) && Array.isArray(configuration.measures) ? { measures: configuration.measures } : {}),
                    ...(configuration.widgetFilters ? { widgetFilters: configuration.widgetFilters } : {}),
                  },
                  ...(shouldRename ? { title: t(`reports.names.${report}`) } : {}),
                });
              }}
            />
          <//>
          <p class="widget-editor-hint">${t(`reports.descriptions.${configuration.report}`)}</p>
          ${type === 'GEO_MAP'
            ? html`<${EditorField} label=${t('geoMap.editor.metric')} icon="IconMapPin" hint=${t('geoMap.editor.metricHint')}>
                <${SelectControl}
                  value=${configuration.metric ?? 'LEADS'}
                  options=${GEO_MAP_METRICS.map((metric) => ({ value: metric, label: t(`geoMap.metrics.${metric}`) }))}
                  onChange=${(metric) => set({ metric })}
                />
              <//>`
            : null}
          ${configuration.report === 'LEAD_QUALITY_FIELDS'
            ? html`<${EditorField} label=${t('reports.leadQualityEditor.entity')} icon="IconUsersGroup">
                <${SelectControl}
                  value=${configuration.entity ?? 'ALL'}
                  options=${LEAD_QUALITY_FIELD_ENTITIES.map((entity) => ({ value: entity, label: t(`reports.leadQualityEntities.${entity}`) }))}
                  onChange=${(entity) => set({ entity: entity ?? 'ALL' })}
                />
              <//>`
            : null}
          ${LIMITED_REPORTS.has(configuration.report)
            ? html`<${EditorField} label=${t('dashboards.editor.limit')} icon="IconListNumbers" hint=${t('reports.editor.limitHint')}>
                <input class="text-input" type="number" min="1" max="100" placeholder=${t('dashboards.editor.limitPlaceholder')}
                  value=${configuration.limit ?? ''}
                  onChange=${(event) => {
                    const value = Number(/** @type {HTMLInputElement} */ (event.currentTarget).value);
                    set({ limit: Number.isFinite(value) && value > 0 ? Math.min(100, Math.round(value)) : undefined });
                  }} />
              <//>`
            : null}
        <//>`
      : null}

    ${isReportWidgetType(type) && LEAD_QUALITY_REPORTS.has(configuration.report)
      ? html`<${EditorSection} title=${t('reports.leadQualityEditor.title')}>
          <${LeadQualityMeasuresEditor} widgetKey=${widget.key} configuration=${configuration} />
        <//>`
      : null}

    ${type === 'TEXT'
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.content')}>
          <${EditorField} label=${t('dashboards.editor.text')} icon="IconAlignLeft" hint=${t('dashboardParity.text.hint')}>
            <div class="widget-editor-rich-text">
              <${RichTextEditor} value=${configuration.markdown ?? ''} allowMentions=${false} placeholder=${t('dashboardParity.text.placeholder')}
                onChange=${(markdown) => set({ markdown })} />
            </div>
          <//>
        <//>`
      : null}

    ${type === 'IFRAME'
      ? html`<${EditorSection} title=${t('dashboardParity.iframe.section')}>
          <${EditorField} label=${t('dashboardParity.iframe.url')} icon="IconLink" hint=${t('dashboardParity.iframe.hint')}>
            <${DebouncedTextInput} value=${configuration.url ?? ''} placeholder="https://exemplo.com/embed" onChange=${(url) => set({ url: url.trim() })} />
          <//>
          ${configuration.url && !checkIframeUrlForThisApp(configuration.url).isValid
            ? html`<p class="widget-editor-error" role="alert">${t(`dashboardParity.iframe.reasons.${/** @type {any} */ (checkIframeUrlForThisApp(configuration.url)).reason}`)}</p>`
            : null}
        <//>`
      : null}

    ${usesObject
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.data')}>
          <${EditorField} label=${t('dashboards.editor.source')} icon="IconDatabase">
            <${SelectControl}
              value=${configuration.objectName}
              searchable=${true}
              options=${objectOptions}
              onChange=${(objectName) => {
                if (!objectName || objectName === configuration.objectName) return;
                updateDraftWidget(widget.key, { configuration: buildDefaultConfiguration(type, { objectName }) });
              }}
            />
          <//>
          ${objectMetadata
            ? html`<${EditorField} label=${t('dashboards.editor.filter')} icon="IconFilter">
                <${WidgetFilterEditor} widget=${widget} objectMetadata=${objectMetadata} />
              <//>`
            : null}
          ${type !== 'TABLE' && objectMetadata
            ? html`<div class="form-field-row">
                <${EditorField} label=${t('dashboards.editor.operation')} icon="IconSum">
                  <${SelectControl} value=${operation}
                    options=${OPERATIONS.filter((candidate) => candidate === 'COUNT' || fields.some((field) => isFieldValidForOperation(field, candidate)))
                      .map((candidate) => ({ value: candidate, label: t(`dashboards.operations.${candidate}`) }))}
                    onChange=${(value) => value && changeOperation(value)} />
                <//>
                ${operation !== 'COUNT'
                  ? html`<${EditorField} label=${t('dashboards.editor.field')} icon="IconNumbers">
                      <${SelectControl} value=${configuration.fieldName} searchable=${true}
                        options=${fields.filter((field) => isFieldValidForOperation(field, operation)).map(toFieldOption)}
                        onChange=${(fieldName) => {
                          const field = fieldByName(fieldName ?? undefined);
                          set({ fieldName, subFieldName: field?.type === FIELD_TYPE.CURRENCY ? 'amountMicros' : undefined,
                            ...(type === 'AGGREGATE' && field?.type === FIELD_TYPE.CURRENCY ? { format: 'CURRENCY' } : {}) });
                        }} />
                    <//>`
                  : null}
              </div>`
            : null}
          ${type === 'TABLE' && objectMetadata
            ? html`<${EditorField} label=${t('dashboards.editor.columns')} icon="IconColumnInsertRight">
                <${TableFieldsEditor} widget=${widget} objectMetadata=${objectMetadata} />
              <//>`
            : null}
        <//>`
      : null}

    ${isChart && objectMetadata
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.grouping')}>
          <${EditorField} label=${type === 'PIE' ? t('dashboards.editor.eachSlice') : t('dashboards.editor.groupBy')} icon="IconLayoutList">
            <${SelectControl} value=${configuration.groupByFieldName} searchable=${true}
              options=${fields.filter(isGroupableField).map(toFieldOption)}
              onChange=${(groupByFieldName) => {
                const field = fieldByName(groupByFieldName ?? undefined);
                const isDate = field ? DATE_FIELD_TYPES.has(field.type) : false;
                set({ groupByFieldName, dateGranularity: isDate ? configuration.dateGranularity ?? 'MONTH' : undefined,
                  orderBy: isDate ? 'FIELD_ASC' : configuration.orderBy });
              }} />
          <//>
          ${isDateGroup
            ? html`<${EditorField} label=${t('dashboards.editor.dateGranularity')} icon="IconCalendar">
                <${SelectControl} value=${configuration.dateGranularity ?? 'MONTH'}
                  options=${DATE_GRANULARITIES.map((value) => ({ value, label: t(`dashboards.granularities.${value}`) }))}
                  onChange=${(dateGranularity) => set({ dateGranularity })} />
              <//>`
            : null}
          ${type !== 'PIE'
            ? html`<${EditorField} label=${t('dashboards.editor.series')} icon="IconChartBarPopular" hint=${t('dashboards.editor.seriesHint')}>
                <${SelectControl} value=${configuration.seriesFieldName} allowEmpty=${true} searchable=${true}
                  options=${fields.filter((field) => isGroupableField(field) && !DATE_FIELD_TYPES.has(field.type) && field.name !== configuration.groupByFieldName).map(toFieldOption)}
                  onChange=${(seriesFieldName) => set({ seriesFieldName: seriesFieldName ?? undefined, ...(seriesFieldName ? {} : { stacked: false }) })} />
              <//>`
            : null}
        <//>`
      : null}

    ${(isChart || type === 'TABLE') && objectMetadata
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.sorting')}>
          ${type === 'TABLE'
            ? html`<div class="form-field-row">
                <${EditorField} label=${t('dashboards.editor.sortField')} icon="IconArrowsSort">
                  <${SelectControl} value=${configuration.orderBy?.[0]?.field} allowEmpty=${true} searchable=${true}
                    options=${fields.filter((field) => (configuration.fields ?? []).includes(field.name) && field.type !== FIELD_TYPE.RELATION).map(toFieldOption)}
                    onChange=${(field) => set({ orderBy: field ? [{ field, direction: configuration.orderBy?.[0]?.direction ?? 'DescNullsLast' }] : undefined })} />
                <//>
                <${EditorField} label=${t('dashboards.editor.direction')} icon="IconSortDescending">
                  <${SelectControl} value=${configuration.orderBy?.[0]?.direction ?? 'DescNullsLast'} disabled=${!configuration.orderBy?.[0]}
                    options=${[{ value: 'AscNullsLast', label: t('viewBar.ascending') }, { value: 'DescNullsLast', label: t('viewBar.descending') }]}
                    onChange=${(direction) => configuration.orderBy?.[0] && set({ orderBy: [{ ...configuration.orderBy[0], direction }] })} />
                <//>
              </div>`
            : html`<${EditorField} label=${t('dashboards.editor.sortBy')} icon="IconArrowsSort">
                <${SelectControl} value=${configuration.orderBy ?? 'FIELD_POSITION_ASC'}
                  options=${ORDER_OPTIONS.map((value) => ({ value, label: t(`dashboards.orderBy.${value}`) }))}
                  onChange=${(orderBy) => set({ orderBy })} />
              <//>`}
          ${type !== 'LINE'
            ? html`<${EditorField} label=${t('dashboards.editor.limit')} icon="IconListNumbers">
                <input class="text-input" type="number" min="1" max=${type === 'TABLE' ? 100 : 100} placeholder=${t('dashboards.editor.limitPlaceholder')}
                  value=${configuration.limit ?? ''}
                  onChange=${(event) => {
                    const value = Number(/** @type {HTMLInputElement} */ (event.currentTarget).value);
                    set({ limit: Number.isFinite(value) && value > 0 ? Math.min(100, Math.round(value)) : undefined });
                  }} />
              <//>`
            : null}
        <//>`
      : null}

    ${(isChart || type === 'AGGREGATE') && objectMetadata
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.appearance')}>
          ${type === 'BAR'
            ? html`<${EditorField} label=${t('dashboards.editor.layout')} icon="IconLayout">
                <${SegmentedControl} value=${configuration.layout ?? 'VERTICAL'}
                  options=${[{ value: 'VERTICAL', label: t('dashboards.editor.vertical') }, { value: 'HORIZONTAL', label: t('dashboards.editor.horizontal') }]}
                  onChange=${(layout) => set({ layout })} />
              <//>
              ${configuration.seriesFieldName ? html`<${ToggleRow} label=${t('dashboards.editor.stacked')} icon="IconChartBarPopular" value=${Boolean(configuration.stacked)} onChange=${(stacked) => set({ stacked })} />` : null}
              <${ToggleRow} label=${t('dashboards.editor.omitNullValues')} icon="IconEyeOff" value=${Boolean(configuration.omitNullValues)} onChange=${(omitNullValues) => set({ omitNullValues })} />`
            : null}
          ${type === 'LINE' && isDateGroup
            ? html`<${ToggleRow} label=${t('dashboards.editor.cumulative')} icon="IconTrendingUp" value=${Boolean(configuration.isCumulative)} onChange=${(isCumulative) => set({ isCumulative })} />`
            : null}
          ${type === 'PIE'
            ? html`<${ToggleRow} label=${t('dashboards.editor.centerMetric')} icon="IconNumber" value=${configuration.showCenterMetric !== false} onChange=${(showCenterMetric) => set({ showCenterMetric })} />`
            : null}
          ${isChart
            ? html`<${ToggleRow} label=${t('dashboards.editor.dataLabels')} icon="IconTag" value=${Boolean(configuration.showDataLabels)} onChange=${(showDataLabels) => set({ showDataLabels })} />`
            : null}
          ${type === 'BAR' || type === 'LINE'
            ? html`<${EditorField} label=${t('dashboards.editor.color')} icon="IconColorSwatch">
                <${SelectControl} value=${configuration.color} allowEmpty=${true} emptyLabel=${t('dashboards.editor.defaultPalette')} searchable=${true}
                  options=${colorOptions} onChange=${(color) => set({ color: color ?? undefined })} />
              <//>`
            : null}
          ${type === 'AGGREGATE'
            ? html`<${EditorField} label=${t('dashboards.editor.format')} icon="IconNumber">
                <${SelectControl} value=${configuration.format ?? 'NUMBER'}
                  options=${['NUMBER', 'CURRENCY', 'PERCENT'].map((value) => ({ value, label: t(`dashboards.formats.${value}`) }))}
                  onChange=${(format) => set({ format })} />
              <//>
              <div class="form-field-row">
                <${EditorField} label=${t('dashboards.editor.prefix')}>
                  <${DebouncedTextInput} value=${configuration.prefix ?? ''} placeholder=${t('dashboards.editor.unitPlaceholder')} onChange=${(prefix) => set({ prefix: prefix || undefined })} />
                <//>
                <${EditorField} label=${t('dashboards.editor.suffix')}>
                  <${DebouncedTextInput} value=${configuration.suffix ?? ''} placeholder=${t('dashboards.editor.unitPlaceholder')} onChange=${(suffix) => set({ suffix: suffix || undefined })} />
                <//>
              </div>`
            : null}
        <//>`
      : null}

    ${usesObject && objectMetadata && dateFields.length > 0
      ? html`<${EditorSection} title=${t('dashboards.editor.sections.period')}>
          <${ToggleRow} label=${t('dashboards.editor.usePeriod')} icon="IconCalendarStats" value=${Boolean(configuration.dateFieldName)}
            onChange=${(enabled) => set({ dateFieldName: enabled ? (dateFields.find((field) => field.name === 'createdAt') ?? dateFields[0]).name : undefined })} />
          ${configuration.dateFieldName
            ? html`<${EditorField} label=${t('dashboards.editor.dateField')} icon="IconCalendarEvent" hint=${t('dashboards.editor.dateFieldHint')}>
                <${SelectControl} value=${configuration.dateFieldName} options=${dateFields.map(toFieldOption)} onChange=${(dateFieldName) => set({ dateFieldName })} />
              <//>`
            : null}
        <//>`
      : null}

    ${!WIDGET_TYPES_WITHOUT_FILTERS.includes(type) && (type === 'KPI' || isReportWidgetType(type) || objectMetadata)
      ? html`<${WidgetFiltersSection} value=${configuration.widgetFilters} onChange=${(widgetFilters) => set({ widgetFilters })} />`
      : null}
  `;
}

/**
 * Texto que só vai ao rascunho depois de uma pausa, para não pedir dados a cada tecla.
 * @param {{ value: string, onChange: (value: string) => void, placeholder?: string }} props
 */
function DebouncedTextInput({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState(value);
  const debounced = useDebouncedValue(draft, 300);
  const lastSentRef = useRef(value);
  useEffect(() => {
    if (debounced === lastSentRef.current) return;
    lastSentRef.current = debounced;
    onChange(debounced);
  }, [debounced]);
  return html`<input class="text-input" value=${draft} placeholder=${placeholder} onInput=${(event) => setDraft(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />`;
}

/**
 * @param {{ onChoose: (type: WidgetType) => void }} props
 */
function WidgetTypePicker({ onChoose }) {
  return html`<div class="widget-type-picker">
    <p class="widget-editor-hint">${t('dashboards.editor.chooseTypeHint')}</p>
    ${WIDGET_TYPE_CHOICES.map((choice) => html`<button type="button" key=${choice.type} class="widget-type-option" onClick=${() => onChoose(choice.type)}>
      <span class="widget-type-option-icon"><${Icon} name=${choice.icon} size=${18} /></span>
      <span class="widget-type-option-texts">
        <span class="widget-type-option-title">${t(`dashboards.widgetTypes.${choice.type}`)}</span>
        <span class="widget-type-option-description">${t(`dashboards.widgetTypeDescriptions.${choice.type}`)}</span>
      </span>
      <${Icon} name="IconChevronRight" size=${14} />
    </button>`)}
  </div>`;
}

/**
 * Página do painel lateral: escolha do tipo (widgetKey nulo) ou configuração de um widget do rascunho.
 * @param {{ widgetKey: string | null }} props
 */
export function WidgetEditorPanel({ widgetKey }) {
  const filters = requestFiltersState.value;
  const widget = widgetKey ? draftWidgetsState.value.find((candidate) => candidate.key === widgetKey) : null;
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const typeAnchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isMoveMenuOpen, setIsMoveMenuOpen] = useState(false);
  const moveAnchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));

  const openWidget = (/** @type {string} */ key) => {
    const current = sidePanelState.value;
    if (current?.page === 'dashboard-widget') sidePanelState.value = { ...current, widgetKey: key };
  };

  if (!widget) {
    return html`<div class="side-panel-page">
      <div class="side-panel-top-bar">
        <div class="side-panel-top-bar-left">
          <span class="side-panel-page-chip"><${Icon} name="IconLayoutGridAdd" size=${16} /><span>${t('dashboards.edit.addWidget')}</span></span>
        </div>
        <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
      </div>
      <div class="side-panel-content">
        <${WidgetTypePicker} onChoose=${(type) => {
          const defaultTitle = type === 'KPI' ? t('dashboards.metrics.NEW_LEADS.label') : isReportWidgetType(type) ? t(`reports.names.${REPORTS_BY_WIDGET_TYPE[type][0]}`) : undefined;
          const created = addDraftWidget(type, { configuration: buildDefaultConfiguration(type), title: defaultTitle });
          openWidget(created.key);
          requestAnimationFrame(() => document.querySelector(`[data-grid-key="${CSS.escape(created.key)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
        }} />
      </div>
    </div>`;
  }

  const typeChoice = WIDGET_TYPE_CHOICES.find((choice) => choice.type === widget.type);
  const tabs = tabsState.value;
  const otherTabs = tabs.filter((tab) => tab.key !== (widget.tabId ?? tabs[0]?.key));
  return html`<div class="side-panel-page widget-editor-panel">
    <div class="side-panel-top-bar">
      <div class="side-panel-top-bar-left">
        <button ref=${typeAnchorRef} type="button" class="side-panel-page-chip widget-editor-type-chip" onClick=${() => setIsTypeMenuOpen(!isTypeMenuOpen)}
          aria-label=${t('dashboards.editor.changeType')} title=${t('dashboards.editor.changeType')}>
          <${Icon} name=${typeChoice?.icon ?? 'IconChartBar'} size=${16} />
          <span>${t(`dashboards.widgetTypes.${widget.type}`)}</span>
          <${Icon} name="IconChevronDown" size=${12} />
        </button>
        <${Dropdown} anchorRef=${typeAnchorRef} isOpen=${isTypeMenuOpen} onClose=${() => setIsTypeMenuOpen(false)} width=${220}>
          <div class="dropdown-menu-items">
            ${WIDGET_TYPE_CHOICES.map((choice) => html`<${MenuItem} key=${choice.type} icon=${choice.icon} text=${t(`dashboards.widgetTypes.${choice.type}`)}
              selected=${choice.type === widget.type}
              onClick=${() => {
                setIsTypeMenuOpen(false);
                if (choice.type === widget.type) return;
                const wasDefaultTitle = widget.title === t(`dashboards.widgetTypes.${widget.type}`);
                changeDraftWidgetType(widget.key, choice.type, buildDefaultConfiguration(choice.type, widget.configuration));
                if (wasDefaultTitle) updateDraftWidget(widget.key, { title: t(`dashboards.widgetTypes.${choice.type}`) });
              }} />`)}
          </div>
        <//>
      </div>
      <${IconButton} icon="IconX" size="small" variant="tertiary" ariaLabel=${t('sidePanel.close')} onClick=${closeSidePanel} />
    </div>
    <div class="side-panel-content widget-editor-content">
      <label class="form-field widget-editor-field">
        <span class="form-field-label"><${Icon} name="IconTypography" size=${14} /><span>${t('dashboards.editor.title')}</span></span>
        <input class="text-input" value=${widget.title} placeholder=${t('dashboards.widget.untitled')}
          onInput=${(event) => updateDraftWidget(widget.key, { title: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
      </label>
      <div class="widget-editor-preview" style=${{ height: `${widget.type === 'KPI' && getKpiMetric(widget.configuration.metric)?.kind === 'NUMBER' || widget.type === 'AGGREGATE' ? 150 : isReportWidgetType(widget.type) ? 340 : 260}px` }}>
        <span class="widget-editor-preview-label">${t('dashboards.editor.preview')}</span>
        <${WidgetCard} widget=${widget} filters=${filters} isPreview=${true} rowSpan=${widget.type === 'AGGREGATE' || widget.type === 'KPI' ? 2 : 4} />
      </div>
      <${WidgetConfigurationForm} key=${widget.key} widget=${widget} />
    </div>
    <div class="side-panel-footer widget-editor-footer">
      <${Button} title=${t('dashboards.widget.remove')} icon="IconTrash" variant="secondary" accent="danger" size="small"
        onClick=${() => { removeDraftWidget(widget.key); closeSidePanel(); }} />
      <${Button} title=${t('dashboards.widget.duplicate')} icon="IconCopyPlus" variant="secondary" size="small"
        onClick=${() => { const copy = duplicateDraftWidget(widget.key); if (copy) openWidget(copy.key); }} />
      ${otherTabs.length > 0
        ? html`<span ref=${moveAnchorRef} title=${t('dashboardParity.tabs.moveWidget')}>
            <${Button} icon="IconArrowRight" ariaLabel=${t('dashboardParity.tabs.moveWidget')} variant="secondary" size="small" onClick=${() => setIsMoveMenuOpen(!isMoveMenuOpen)} />
          </span>
          <${Dropdown} anchorRef=${moveAnchorRef} isOpen=${isMoveMenuOpen} onClose=${() => setIsMoveMenuOpen(false)} placement="top-start" width=${220}>
            <div class="dropdown-menu-items">
              ${otherTabs.map((tab) => html`<${MenuItem} key=${tab.key} icon="IconAppWindow" text=${tab.title}
                onClick=${() => { setIsMoveMenuOpen(false); moveDraftWidgetToTab(widget.key, tab.key); }} />`)}
            </div>
          <//>`
        : null}
      <span class="widget-editor-footer-spacer"></span>
      <${Button} title=${t('dashboards.editor.done')} accent="blue" size="small" onClick=${closeSidePanel} />
    </div>
  </div>`;
}
